import { useEffect, useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Eye,
  Radio,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Target,
  ChevronRight
} from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { api } from '@/lib/api';
import { formatCurrency, formatNumber, formatPercent, pad2, todayStr } from '@/lib/utils';
import type { DashboardStats } from '@shared/types';

function useDashboard() {
  const [data, setData] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api
      .getDashboardStats()
      .then((d) => setData(d))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  return { data, error, loading, reload: load };
}

export default function DashboardPage() {
  const { data, error, loading } = useDashboard();

  if (loading) {
    return (
      <div className="text-foreground font-space uppercase tracking-widest text-sm py-32 text-center">
        LOADING<span className="text-primary">_</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="border-2 border-destructive p-8 text-destructive font-space">
        <AlertTriangle className="inline-block mr-2" />
        加载失败:{error ?? 'unknown'}
      </div>
    );
  }

  const today = todayStr();
  const reportPeriod = dayjs().format('YYYY.MM.DD');

  // Marquee 数据
  const marqueeItems = [
    { label: '今日GMV', value: formatCurrency(data.todayGMV) },
    { label: '直播观看', value: formatNumber(data.liveViewers) },
    { label: '转化率', value: formatPercent(data.conversionRate) },
    { label: '活跃主播', value: formatNumber(data.activeHosts) },
    { label: '今日场次', value: formatNumber(data.todayStreams) }
  ];

  const kpis = [
    {
      title: '今日GMV',
      value: formatCurrency(data.todayGMV),
      change: data.todayGMVChange,
      icon: <ShoppingCart className="w-6 h-6" strokeWidth={2} />
    },
    {
      title: '观看人数',
      value: formatNumber(data.liveViewers),
      change: data.liveViewersChange,
      icon: <Eye className="w-6 h-6" strokeWidth={2} />
    },
    {
      title: '转化率',
      value: formatPercent(data.conversionRate),
      change: data.conversionRateChange,
      icon: <Target className="w-6 h-6" strokeWidth={2} />
    },
    {
      title: '直播场次',
      value: formatNumber(data.todayStreams),
      change: data.todayStreamsChange,
      icon: <Radio className="w-6 h-6" strokeWidth={2} />
    }
  ];

  // 趋势图配置
  const trendOption = {
    backgroundColor: 'transparent',
    grid: { top: 30, right: 24, bottom: 30, left: 60 },
    tooltip: { trigger: 'axis', backgroundColor: '#000', borderColor: '#444', textStyle: { color: '#fff' } },
    legend: { textStyle: { color: '#aaa' }, top: 0 },
    xAxis: {
      type: 'category',
      data: data.gmvTrend.dates.map((d) => dayjs(d).format('MM/DD')),
      axisLine: { lineStyle: { color: '#444' } },
      axisLabel: { color: '#aaa' }
    },
    yAxis: [
      {
        type: 'value',
        name: 'GMV',
        axisLine: { lineStyle: { color: '#444' } },
        splitLine: { lineStyle: { color: '#222' } },
        axisLabel: { color: '#aaa', formatter: (v: number) => formatCurrency(v) }
      },
      {
        type: 'value',
        name: '观看',
        axisLine: { lineStyle: { color: '#444' } },
        splitLine: { show: false },
        axisLabel: { color: '#aaa', formatter: (v: number) => formatNumber(v) }
      }
    ],
    series: [
      {
        name: 'GMV',
        type: 'line',
        data: data.gmvTrend.gmv,
        smooth: true,
        lineStyle: { width: 3, color: '#dafa1d' },
        itemStyle: { color: '#dafa1d' },
        areaStyle: { color: 'rgba(218,250,29,0.1)' }
      },
      {
        name: '观看',
        type: 'line',
        yAxisIndex: 1,
        data: data.gmvTrend.views,
        smooth: true,
        lineStyle: { width: 2, color: '#f5f5f5', type: 'dashed' },
        itemStyle: { color: '#f5f5f5' }
      }
    ]
  };

  const aiInsights = [
    {
      type: 'success',
      title: '晚间黄金档流量稳定',
      content: '本周 20:00-22:00 时段 GMV 占比达 42%,建议加排该时段直播密度。',
      icon: <CheckCircle2 className="w-5 h-5" />
    },
    {
      type: 'tip',
      title: '自然流量有提升空间',
      content: '近 7 天自然流量占比 35%,通过短视频预热 + 私域引流预计可提升至 45%。',
      icon: <Lightbulb className="w-5 h-5" />
    },
    {
      type: 'warning',
      title: '3 位主播转化率低于均值',
      content: '建议优化选品结构,加强话术培训,周内完成针对性辅导。',
      icon: <AlertTriangle className="w-5 h-5" />
    },
    {
      type: 'info',
      title: '关注 6 月品牌日节奏',
      content: '距离品牌日还有 8 天,建议提前 3 天开始预热短视频发布。',
      icon: <Sparkles className="w-5 h-5" />
    }
  ];

  return (
    <div>
      {/* 顶部品牌标题区 */}
      <header className="border-b-2 border-border pb-8 mb-8">
        <div className="text-xs uppercase tracking-widest text-primary font-space mb-2">
          OPERATIONS / {reportPeriod}
        </div>
        <h1 className="text-[clamp(3rem,12vw,8rem)] font-bold leading-[0.85] tracking-tighter uppercase font-space text-foreground">
          运营<br />概览<span className="text-primary">.</span>
        </h1>
        <p className="mt-4 text-muted-foreground text-base md:text-lg max-w-2xl">
          一站式直播业务监控。今日整体表现健康,4 个核心指标均超周均。
        </p>
      </header>

      {/* 数据跑马灯 */}
      <div className="w-full bg-primary text-black border-y-2 border-border py-3 overflow-hidden mb-12">
        <div className="marquee-track flex whitespace-nowrap">
          {[...marqueeItems, ...marqueeItems, ...marqueeItems].map((item, i) => (
            <div key={i} className="flex items-center gap-6 px-6">
              <span className="text-2xl md:text-4xl font-bold font-space uppercase">
                {item.label}
              </span>
              <span className="text-2xl md:text-4xl font-bold font-space uppercase tabular-nums">
                {item.value}
              </span>
              <span className="text-primary-foreground">◆</span>
            </div>
          ))}
        </div>
      </div>

      {/* 核心 KPI 卡片 */}
      <section className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-4 h-4 bg-primary" />
          <h2 className="text-3xl md:text-5xl font-bold font-space uppercase leading-none">
            核心指标
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
          {kpis.map((kpi) => (
            <div
              key={kpi.title}
              className="border-2 border-border bg-background p-6 md:p-8 flex flex-col justify-between min-h-[200px] transition-all duration-300 hover:bg-primary hover:border-primary hover:text-black group cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground group-hover:text-black">
                  {kpi.title}
                </span>
                <span className="text-foreground group-hover:text-black">{kpi.icon}</span>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold font-space tracking-tighter leading-none tabular-nums">
                  {kpi.value}
                </div>
                <div
                  className={`mt-3 flex items-center gap-1 text-sm font-bold ${
                    kpi.change >= 0 ? 'text-primary group-hover:text-black' : 'text-destructive group-hover:text-black'
                  }`}
                >
                  {kpi.change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {Math.abs(kpi.change)}% VS 昨日
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* GMV 趋势 + 直播动态 */}
      <section className="mb-12 grid grid-cols-1 lg:grid-cols-3 gap-0 border-2 border-border">
        <div className="lg:col-span-2 p-8 md:p-12 border-r-2 border-border">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-4 h-4 bg-primary" />
            <h2 className="text-2xl md:text-3xl font-bold font-space uppercase leading-none">
              GMV / 观看趋势
            </h2>
            <span className="ml-auto text-xs uppercase tracking-widest text-muted-foreground">
              近 7 天
            </span>
          </div>
          <ReactECharts option={trendOption} style={{ height: 320 }} />
        </div>
        <div className="p-8 md:p-12 bg-background">
          <div className="flex items-center gap-3 mb-6">
            <BarChart3 className="w-5 h-5 text-primary" />
            <h2 className="text-2xl font-bold font-space uppercase leading-none">
              策划统计
            </h2>
          </div>
          <div className="space-y-3">
            {[
              { label: '总策划', value: data.planningStats.total, color: 'text-foreground' },
              { label: '策划中', value: data.planningStats.planning, color: 'text-info' },
              { label: '筹备中', value: data.planningStats.preparing, color: 'text-warning' },
              { label: '已完成', value: data.planningStats.completed, color: 'text-primary' }
            ].map((row) => (
              <div
                key={row.label}
                className="border-2 border-border p-4 flex items-center justify-between hover:bg-accent transition-colors duration-200"
              >
                <span className="text-sm uppercase tracking-widest font-bold text-muted-foreground">
                  {row.label}
                </span>
                <span className={`text-3xl font-bold font-space tabular-nums ${row.color}`}>
                  {pad2(row.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top 主播 + 今日排行 */}
      <section className="mb-12 grid grid-cols-1 lg:grid-cols-2 gap-0">
        {/* Top 主播 */}
        <div className="border-2 border-border bg-background p-8 md:p-12 hover:border-primary transition-colors duration-300">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-4 h-4 bg-primary" />
            <h2 className="text-2xl md:text-3xl font-bold font-space uppercase leading-none">
              本周 Top 主播
            </h2>
          </div>
          <div className="space-y-2">
            {data.topHosts.map((host, i) => (
              <div
                key={host.id}
                className="border-b-2 border-border pb-3 flex items-center gap-4 group hover:bg-primary hover:text-black px-3 -mx-3 transition-colors duration-0"
              >
                <span className="font-space font-bold text-2xl w-10 tabular-nums">
                  {pad2(i + 1)}
                </span>
                <div className="flex-1">
                  <div className="font-bold uppercase tracking-tight">{host.name}</div>
                  <div className="text-xs text-muted-foreground group-hover:text-black uppercase tracking-widest">
                    {host.liveCount} 场 · 同比 {host.change >= 0 ? '+' : ''}{host.change}%
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold font-space tabular-nums text-xl">{formatCurrency(host.gmv)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 今日直播动态 */}
        <div className="border-2 border-l-0 lg:border-l-2 border-border bg-background p-8 md:p-12 hover:border-primary transition-colors duration-300">
          <div className="flex items-center gap-3 mb-6">
            <Radio className="w-5 h-5 text-primary" />
            <h2 className="text-2xl md:text-3xl font-bold font-space uppercase leading-none">
              直播动态
            </h2>
            <span className="ml-auto text-xs uppercase tracking-widest text-muted-foreground">
              {today}
            </span>
          </div>
          <div className="space-y-3">
            {data.liveStreams.slice(0, 6).map((s) => (
              <div
                key={s.id}
                className="border-2 border-border p-4 hover:bg-primary hover:text-black transition-colors duration-200 group cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className={`text-xs font-bold uppercase tracking-widest px-2 py-1 border-2 ${
                      s.status === 'live'
                        ? 'border-destructive text-destructive group-hover:text-black group-hover:border-black animate-pulse'
                        : s.status === 'upcoming'
                        ? 'border-info text-info group-hover:text-black group-hover:border-black'
                        : 'border-muted-foreground text-muted-foreground group-hover:text-black group-hover:border-black'
                    }`}
                  >
                    {s.status === 'live' ? '● 直播中' : s.status === 'upcoming' ? '○ 即将开播' : '◇ 策划中'}
                  </span>
                  <span className="text-xs text-muted-foreground group-hover:text-black font-space">
                    {s.time}
                  </span>
                </div>
                <div className="font-bold uppercase tracking-tight text-base mb-1">{s.title}</div>
                <div className="text-xs text-muted-foreground group-hover:text-black flex items-center gap-3">
                  <span>主播:{s.hostName}</span>
                  <span>·</span>
                  <span>{formatNumber(s.viewers)} 观看</span>
                  {s.gmv > 0 && (
                    <>
                      <span>·</span>
                      <span className="font-bold">{formatCurrency(s.gmv)}</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI 运营洞察 */}
      <section className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-4 h-4 bg-primary" />
          <h2 className="text-3xl md:text-5xl font-bold font-space uppercase leading-none">
            AI 运营洞察
          </h2>
          <span className="ml-auto text-xs uppercase tracking-widest text-muted-foreground">
            由 Codex 规则引擎生成
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {aiInsights.map((insight, i) => (
            <div
              key={i}
              className={`p-6 md:p-8 border-2 border-border group cursor-pointer transition-colors duration-200 ${
                i === 0 ? 'bg-primary text-black border-primary hover:bg-background hover:text-foreground' : 'hover:border-primary'
              }`}
            >
              <div className="flex items-start gap-4">
                <span className="font-space font-bold text-3xl tabular-nums opacity-50">
                  {pad2(i + 1)}
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {insight.icon}
                    <span className="font-bold uppercase tracking-tight text-lg">{insight.title}</span>
                  </div>
                  <p className={`text-sm leading-relaxed ${i === 0 ? 'text-black' : 'text-muted-foreground group-hover:text-foreground'}`}>
                    {insight.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
